/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable unused-imports/no-unused-vars */
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Box, Container, Button } from '@mui/material';
import Typography from '@/components/typography/typography';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { EnhancedSectionData } from '../types/editor';
import { generateEmailPreview } from '../utils/common';
import FreeCanvas from './EditorCanvas/FreeCanvas';
import { PBPaper } from '../engine/paginator';

interface EditorCanvasProps {
  sections: EnhancedSectionData[];
  activeId: string | null;
  selectedSection: string | null;
  onSelectSection: (id: string | null) => void;
  onUpdateSection: (id: string, data: any) => void;
  onDeleteSection: (id: string) => void;
  onMoveElement: (id: string, x: number, y: number) => void;
  onResizeElement: (id: string, x: number, y: number, width: number, height: number) => void;
  onRotateElement: (id: string, rotation: number) => void;
  onBringForward: (id: string) => void;
  onSendBackward: (id: string) => void;
  preview: boolean;
  editHtml: boolean;
  onApplyHtmlEdit: (html: string) => void;
  onCancelHtmlEdit: () => void;
  placeholders: Record<string, any>[];
  stConfig?: any;
  stationeryHeaderHtml?: string;
  stationeryFooterHtml?: string;
  zoom?: number;
  paper?: string;
  orient?: string;
  watermark?: {
    show: boolean;
    opacity: number;
    size: number;
    rotation: number;
    image?: string;
  };
  // Multi-page props
  pages?: { id: string; elements: EnhancedSectionData[] }[];
  currentPageIndex?: number;
  onSetCurrentPage?: (idx: number) => void;
  onAddPage?: () => void;
  onDeletePage?: (idx: number) => void;
  onUpdatePageSections?: (pageIdx: number, sections: EnhancedSectionData[]) => void;
  onMoveElementToPage?: (id: string, offset: -1 | 1) => void;
}

const EditorCanvas: React.FC<EditorCanvasProps> = ({
  sections,
  activeId,
  selectedSection,
  onSelectSection,
  onUpdateSection,
  onDeleteSection,
  onMoveElement,
  onResizeElement,
  onRotateElement,
  onBringForward,
  onSendBackward,
  preview,
  editHtml,
  onApplyHtmlEdit,
  onCancelHtmlEdit,
  placeholders,
  stConfig,
  stationeryHeaderHtml = '',
  stationeryFooterHtml = '',
  zoom = 100,
  paper = 'A4',
  orient = 'portrait',
  watermark,
  // Multi-page
  pages,
  currentPageIndex = 0,
  onSetCurrentPage,
  onAddPage,
  onDeletePage,
  onUpdatePageSections,
  onMoveElementToPage,
}) => {
  const [htmlEditValue, setHtmlEditValue] = useState('');

  // Sync textarea when entering HTML edit mode
  useEffect(() => {
    if (editHtml) {
      setHtmlEditValue(generateEmailPreview(sections, [], [], false, undefined, stationeryHeaderHtml, stationeryFooterHtml, paper, orient, watermark));
    }
  }, [editHtml]);

  // Paper dimensions
  const D = useMemo(() => {
    try {
      return PBPaper.dims(paper, orient);
    } catch {
      let w = paper === 'Letter' ? 816 : 794;
      let h = paper === 'Letter' ? 1056 : 1123;
      if (orient === 'landscape') { const t = w; w = h; h = t; }
      return { w, h, cw: w - 104 };
    }
  }, [paper, orient]);

  // Header height in px (same logic for every page)
  const headerHeight = useMemo(() => {
    if (!stationeryHeaderHtml) return 0;
    if (stConfig?.kind === 'preset' && stConfig.preset?.header?.show) {
      return stConfig.preset?.header?.height || stConfig.preset?.reserve?.top || 156;
    }
    if (stConfig?.kind === 'preprinted' && stConfig.preset) {
      return stConfig.preset.reserve?.top || 150;
    }
    return 156;
  }, [stConfig, stationeryHeaderHtml]);

  // Footer height in px
  const footerHeight = useMemo(() => {
    if (!stationeryFooterHtml) return 0;
    if (stConfig?.kind === 'preset' && stConfig.preset?.footer?.show) {
      return stConfig.preset?.footer?.height || stConfig.preset?.reserve?.bottom || 120;
    }
    if (stConfig?.kind === 'preprinted' && stConfig.preset) {
      return stConfig.preset.reserve?.bottom || 110;
    }
    return 120;
  }, [stConfig, stationeryFooterHtml]);

  // Fixed content height per page — hard boundary, NO Math.max with maxSectionBottom
  const contentHeight = useMemo(() => {
    return Math.max(100, D.h - headerHeight - footerHeight);
  }, [D.h, headerHeight, footerHeight]);

  // Legacy: for single-page or non-multi-page mode, keep backward compat
  const maxSectionBottom = useMemo(() => {
    if (!sections || sections.length === 0) return 0;
    return Math.max(0, ...sections.filter(s => !s.hidden).map(s => (s.y ?? 0) + (s.height ?? 60)));
  }, [sections]);

  // availableCanvasHeight: fixed (content height) when in multi-page mode, flexible in single-page legacy
  const isMultiPage = pages && pages.length > 0;
  const availableCanvasHeight = isMultiPage ? contentHeight : Math.max(contentHeight, maxSectionBottom + 50);

  const formattedPlaceholders = useMemo(() =>
    placeholders.map((p: any) => ({ value: p.value || p.key || '', label: p.label || p.name || String(p.value || p.key || '') })),
    [placeholders]
  );

  const isDraggingFromSidebar = !!(activeId && String(activeId).includes('sidebar-'));

  // ─── HTML EDIT MODE ──────────────────────────────────────────────────────────
  const renderHtmlEditor = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        px: 2, py: 1, bgcolor: '#1e1e1e', borderBottom: '1px solid #333', borderRadius: '4px 4px 0 0',
      }}>
        <Typography type='s3' sx={{ color: '#ccc', fontFamily: 'monospace' }}>
          HTML Source Editor — changes will replace current sections
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size='small' variant='contained' color='primary' startIcon={<CheckIcon fontSize='small' />}
            onClick={() => onApplyHtmlEdit(htmlEditValue)} sx={{ boxShadow: 'none' }}>
            Apply
          </Button>
          <Button size='small' variant='outlined' startIcon={<CloseIcon fontSize='small' />}
            onClick={() => onCancelHtmlEdit()}
            sx={(theme) => ({ color: `${theme.palette.theme.secondary[1000]} !important`, borderColor: '#555 !important' })}>
            Cancel
          </Button>
        </Box>
      </Box>
      <textarea
        value={htmlEditValue}
        onChange={(e) => setHtmlEditValue(e.target.value)}
        spellCheck={false}
        style={{
          flex: 1, width: '100%', minHeight: 'calc(100vh - 220px)', padding: '16px',
          fontFamily: '"Fira Code", "Cascadia Code", "Consolas", monospace', fontSize: '13px',
          lineHeight: '1.6', color: '#d4d4d4', backgroundColor: '#1e1e1e', border: 'none',
          outline: 'none', resize: 'none', boxSizing: 'border-box', borderRadius: '0 0 4px 4px', tabSize: 2,
        }}
      />
    </Box>
  );

  // ─── PREVIEW MODE ────────────────────────────────────────────────────────────
  const renderPreview = () => {
    try {
      const pageList = (pages && pages.length > 0) ? pages : [{ id: 'page-1', elements: sections }];
      return (
        <Box sx={{
          transform: `scale(${zoom / 100})`, transformOrigin: 'top center',
          transition: 'transform 0.2s ease-in-out', margin: '0 auto', overflowX: 'auto',
          display: 'flex', flexDirection: 'column', gap: '24px', pb: 4, alignItems: 'center'
        }}>
          {pageList.map((page, pageIdx) => {
            const hHtml = headerShownOnPage(pageIdx) ? getHeaderHtmlForPage(pageIdx) : '';
            const fHtml = footerShownOnPage(pageIdx) ? stationeryFooterHtml : '';
            return (
              <Box key={page.id} dangerouslySetInnerHTML={{
                __html: generateEmailPreview(page.elements, [], [], false, undefined, hHtml, fHtml, paper, orient, watermark)
              }} />
            );
          })}
        </Box>
      );
    } catch (err: any) {
      return <Box sx={{ p: 4, color: 'red' }}>Preview Error: {err.message || String(err)}</Box>;
    }
  };

  // Helper: should the header show on a given page index?
  const headerShownOnPage = useCallback((pageIdx: number) => {
    if (!stationeryHeaderHtml) return false;
    if (stConfig?.kind === 'preprinted') return pageIdx === 0;
    const repeat = stConfig?.preset?.header?.repeat || 'all';
    return repeat === 'all' || pageIdx === 0;
  }, [stConfig, stationeryHeaderHtml]);

  // Helper: header html for a given page (compact on pages 2+ when compactCont is on)
  const getHeaderHtmlForPage = useCallback((pageIdx: number) => {
    if (!headerShownOnPage(pageIdx)) return '';
    if (stConfig?.kind === 'preset' && stConfig.preset?.header?.compactCont && pageIdx > 0) {
      // Compact header — the parent already renders compact via stationeryHeaderHtml
      // For now return the same HTML; a future enhancement could render a compact version
      return stationeryHeaderHtml;
    }
    return stationeryHeaderHtml;
  }, [stConfig, stationeryHeaderHtml, headerShownOnPage]);

  // Helper: should the footer show on a given page index?
  const footerShownOnPage = useCallback((pageIdx: number) => {
    if (!stationeryFooterHtml) return false;
    if (stConfig?.kind === 'preprinted') return pageIdx === 0;
    const repeat = stConfig?.preset?.footer?.repeat || 'all';
    return repeat === 'all' || pageIdx === 0;
  }, [stConfig, stationeryFooterHtml]);

  // ─── EDIT MODE (FREE CANVAS — MULTI-PAGE) ───────────────────────────────────
  const renderEditCanvas = () => {
    try {
      const pageList = (pages && pages.length > 0) ? pages : [{ id: 'page-1', elements: sections }];

      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '24px', pb: 4 }}>
          {pageList.map((page, pageIdx) => {
            const pageElements = page.elements;
            const showH = headerShownOnPage(pageIdx);
            const hHtml = showH ? getHeaderHtmlForPage(pageIdx) : '';
            const showF = footerShownOnPage(pageIdx);
            const isActivePage = pageIdx === currentPageIndex;

            const pageHeaderHeight = showH ? headerHeight : 0;
            const pageFooterHeight = showF ? footerHeight : 0;
            const pageContentHeight = D.h - pageHeaderHeight - pageFooterHeight;

            return (
              <Box
                key={page.id}
                onClick={() => onSetCurrentPage && onSetCurrentPage(pageIdx)}
                className="st pbd-page pb-tpl-view"
                style={{
                  width: `${D.w}px`,
                  minWidth: `${D.w}px`,
                  maxWidth: `${D.w}px`,
                  height: `${D.h}px`,
                  minHeight: `${D.h}px`,
                  maxHeight: `${D.h}px`,
                  overflow: 'hidden',
                }}
                sx={{
                  position: 'relative',
                  width: `${D.w}px !important`,
                  minWidth: `${D.w}px !important`,
                  maxWidth: `${D.w}px !important`,
                  display: 'flex',
                  flexDirection: 'column',
                  boxShadow: isActivePage ? '0 0 0 2px #6366f1, 0 4px 24px rgba(0,0,0,0.15)' : '0 4px 24px rgba(0,0,0,0.12)',
                  border: isActivePage
                    ? '2px solid #6366f1'
                    : (activeId && String(activeId).includes('sidebar-') ? '2px dashed #6366f1' : '1px solid #e5e7eb'),
                  borderRadius: 0,
                  bgcolor: '#fff',
                  cursor: isActivePage ? 'default' : 'pointer',
                  transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
                  flexShrink: 0,
                }}
              >
                {/* Page number badge */}
                <Box sx={{
                  position: 'absolute', top: 4, right: 8, zIndex: 100,
                  fontSize: 10, color: isActivePage ? '#6366f1' : '#9ca3af',
                  fontWeight: 600, letterSpacing: '0.05em', pointerEvents: 'none',
                  userSelect: 'none',
                }}>PAGE {pageIdx + 1}</Box>

                {/* Stationery header */}
                {hHtml && (
                  <Box
                    sx={{ width: '100%', zIndex: 0, flexShrink: 0, pointerEvents: 'none' }}
                    dangerouslySetInnerHTML={{ __html: hHtml }}
                  />
                )}

                {/* Free canvas content area — hard fixed height */}
                <Box sx={{ position: 'relative', width: '100%', flexShrink: 0, height: pageContentHeight, overflow: 'hidden', pointerEvents: isActivePage ? 'auto' : 'none' }}>
                  <FreeCanvas
                    sections={pageElements}
                    selectedId={isActivePage ? selectedSection : null}
                    canvasWidth={D.w}
                    canvasHeight={pageContentHeight}
                    onSelectElement={onSelectSection}
                    onMove={(id, x, y) => {
                      const section = pageElements.find(s => s.id === id);
                      const clampedY = Math.max(0, Math.min(y, pageContentHeight - (section?.height ?? 60)));
                      onMoveElement(id, x, clampedY);
                    }}
                    onResize={(id, x, y, w, h) => {
                      const clampedY = Math.max(0, Math.min(y, pageContentHeight - h));
                      onResizeElement(id, x, clampedY, w, h);
                    }}
                    onRotate={onRotateElement}
                    onDelete={onDeleteSection}
                    onUpdate={(id, content, extra) => onUpdateSection(id, { content, ...extra })}
                    onBringForward={onBringForward}
                    onSendBackward={onSendBackward}
                    placeholders={formattedPlaceholders}
                    isDraggingFromSidebar={!!(isActivePage && activeId && String(activeId).includes('sidebar-'))}
                    watermark={watermark}
                    onMoveToPage={onMoveElementToPage}
                    pageIndex={pageIdx}
                    totalPages={pageList.length}
                  />
                </Box>

                {/* Stationery footer */}
                {showF && stationeryFooterHtml && (
                  <Box
                    sx={{ width: '100%', zIndex: 0, flexShrink: 0, mt: 'auto', pointerEvents: 'none' }}
                    dangerouslySetInnerHTML={{ __html: stationeryFooterHtml }}
                  />
                )}
              </Box>
            );
          })}
        </Box>
      );
    } catch (err: any) {
      return <Box sx={{ p: 4, color: 'red' }}>Edit Canvas Error: {err.message || String(err)}</Box>;
    }
  };

  return (
    <Box sx={{ display: 'flex', flex: 1, overflow: 'visible', justifyContent: 'center', width: '100%' }}>
      <Container maxWidth={false} disableGutters sx={{ py: editHtml ? 0 : 2, height: editHtml ? '100%' : undefined, display: 'flex', justifyContent: 'center', width: '100%' }}>
        {editHtml ? renderHtmlEditor() : preview ? renderPreview() : renderEditCanvas()}
      </Container>
    </Box>
  );
};

export default EditorCanvas;